import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ContentSafeAreaView } from '../../components';
import { useLocation, useNavigate } from 'react-router-dom';
import Spinner from '../../components/Spinner/Spinner';
import { selectUser } from '../../store/slices/user/slice';
import { getSafeAreaInset, triggerHaptic } from '../../utils/telegram';
import { INFLUENCER } from '../../components/App/routes';
import ReelCommentsModal from '../../components/Reels/ReelCommentsModal/ReelCommentsModal';
import ReelShareModal from '../../components/Reels/ReelShareModal/ReelShareModal';
import ReelFeedHeader from '../../components/Reels/ReelFeedHeader/ReelFeedHeader';
import { EmptyState, LoaderContainer, ReelStage, ReelViewport } from './ReelsPage.styles';
import { DEFAULT_FEED_TAB, FEED_TABS } from './constants';
import useReelComments from './hooks/useReelComments';
import useReelsFeed from './hooks/useReelsFeed';
import useReelShare from './hooks/useReelShare';
import ReelsFeedList from './components/ReelsFeedList';
import { likeFeedItem, unlikeFeedItem } from '../../store/slices/feed/asyncThunks';
import { resetFeedInfo, resetTrendingInfo } from '../../store/slices/feed/slice';
import { markTrendingItemSeen } from '../../utils/trendingSeen';
import { notifyError } from '../../utils/notify';
import { setInfluencer } from '../../store/slices/influencer/slice';

const RETURN_REEL_STORAGE_KEY = 'reels:return-reel-id';

const getStoredReturnReelId = () => {
  try {
    return window.sessionStorage.getItem(RETURN_REEL_STORAGE_KEY);
  } catch (error) {
    return null;
  }
};

const clearStoredReturnReelId = () => {
  try {
    window.sessionStorage.removeItem(RETURN_REEL_STORAGE_KEY);
  } catch (error) {
    // Ignore storage failures and continue.
  }
};

const storeReturnReelId = reelId => {
  if (!reelId) {
    return;
  }

  try {
    window.sessionStorage.setItem(RETURN_REEL_STORAGE_KEY, reelId);
  } catch (error) {
    // Ignore storage failures and fall back to the default top-of-feed behavior.
  }
};

const ReelsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(selectUser);
  const sharedReelId = location.state?.sharedReelId || null;
  const [targetReelId, setTargetReelId] = useState(() => sharedReelId || getStoredReturnReelId());
  const [activeTab, setActiveTab] = useState(DEFAULT_FEED_TAB);
  const { top: safeAreaTop } = getSafeAreaInset();
  const activeReelIdRef = useRef(sharedReelId || null);
  const { reels, pending, loaded, fetchMorePending, hasMore, loadMoreRef } = useReelsFeed(targetReelId, activeTab);
  const commentsModal = useReelComments({ reels, user });
  const shareModal = useReelShare(reels);

  useEffect(() => {
    if (!sharedReelId) {
      return;
    }

    setTargetReelId(sharedReelId);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, navigate, sharedReelId]);

  useEffect(() => {
    return () => {
      storeReturnReelId(activeReelIdRef.current);
    };
  }, []);

  const onLikeToggle = async reel => {
    if (reel.likePending) {
      return;
    }

    triggerHaptic();

    try {
      if (reel.liked) {
        await dispatch(unlikeFeedItem({ feedItemId: reel.id })).unwrap();
      } else {
        await dispatch(likeFeedItem({ feedItemId: reel.id })).unwrap();
      }
    } catch (error) {
      notifyError(error.message || 'Unable to update the reel like right now.');
    }
  };

  const openInfluencerProfile = reel => {
    if (!reel.profileRouteId) {
      return;
    }

    storeReturnReelId(reel.id);
    triggerHaptic();
    dispatch(setInfluencer(null));
    navigate(`/${INFLUENCER.replace(':id', reel.profileRouteId)}`);
  };

  const onTabChange = tab => {
    if (tab === activeTab) {
      return;
    }

    triggerHaptic();
    clearStoredReturnReelId();
    setTargetReelId(null);
    activeReelIdRef.current = null;

    // Reset the entered tab so it refetches from the first page and starts at index 0.
    dispatch(tab === FEED_TABS.TRENDING ? resetTrendingInfo() : resetFeedInfo());
    setActiveTab(tab);
  };

  let feedBody;

  if ((!loaded || pending) && !reels.length) {
    feedBody = (
      <LoaderContainer>
        <Spinner width={150} height={150} />
      </LoaderContainer>
    );
  } else if (loaded && !reels.length) {
    feedBody = <EmptyState>No reels available yet.</EmptyState>;
  } else {
    feedBody = (
      <ReelsFeedList
        key={activeTab}
        reels={reels}
        emptyStateMessage="No reels available yet."
        onAvatarClick={openInfluencerProfile}
        onLikeToggle={onLikeToggle}
        onOpenComments={reel => {
          if (reel.commentsAllowed) {
            commentsModal.openComments(reel.id);
          }
        }}
        onOpenShare={reel => shareModal.openShare(reel.id)}
        targetReelId={targetReelId}
        onTargetReelLocated={() => {
          clearStoredReturnReelId();
          setTargetReelId(null);
        }}
        onActiveReelChange={reelId => {
          activeReelIdRef.current = reelId;

          if (reelId && activeTab === FEED_TABS.TRENDING) {
            const activeReel = reels.find(reel => reel.id === reelId);

            if (activeReel?.isTrending) {
              markTrendingItemSeen(reelId);
            }
          }
        }}
        hasMore={hasMore}
        loadMoreRef={loadMoreRef}
        fetchMorePending={fetchMorePending}
      />
    );
  }

  return (
    <>
      <ContentSafeAreaView hideLogo removeTop removeBottom hideScrollbar>
        <ReelViewport>
          <ReelStage>
            <ReelFeedHeader activeTab={activeTab} tabs={FEED_TABS} onTabClick={onTabChange} topOffset={safeAreaTop} />
            {feedBody}
          </ReelStage>
        </ReelViewport>
      </ContentSafeAreaView>

      <ReelCommentsModal
        isOpen={commentsModal.isCommentsOpen}
        onClose={commentsModal.closeComments}
        commentsCount={commentsModal.commentsCount}
        comments={commentsModal.comments}
        isLoading={commentsModal.isLoading}
        isSubmitting={commentsModal.isSubmitting}
        composerPlaceholder={commentsModal.composerPlaceholder}
        replyTargetName={commentsModal.replyTargetName}
        commentText={commentsModal.commentText}
        onCommentTextChange={commentsModal.setCommentText}
        onCommentSubmit={commentsModal.onCommentSubmit}
        onCommentLikeToggle={commentsModal.onCommentLikeToggle}
        onReplyClick={commentsModal.onReplyClick}
        onCancelReply={commentsModal.onCancelReply}
        onDeleteComment={commentsModal.onDeleteComment}
        onLoadMoreComments={commentsModal.onLoadMoreComments}
        commentsHasMore={commentsModal.commentsHasMore}
        commentsFetchMorePending={commentsModal.commentsFetchMorePending}
        onLoadReplies={commentsModal.onLoadReplies}
        onLoadMoreReplies={commentsModal.onLoadMoreReplies}
      />
      <ReelShareModal
        isOpen={shareModal.isShareOpen}
        onClose={shareModal.closeShare}
        onShareOptionClick={shareModal.onShareOptionClick}
      />
    </>
  );
};

export default ReelsPage;
